import {Component, EventEmitter, Input, Output} from '@angular/core';
import {AdminDashboardKpi} from '@features/admin-panel/models/admin-dashboard.models';
import {
  KpiTone,
  kpiAsOfText,
  kpiCollectingText,
  kpiDeltaText,
  kpiTone,
  kpiUnitText,
  kpiValueText
} from './admin-dashboard-kpi-tile.format';

/**
 * The section a click on a KPI tile scrolls to, by KPI key (OCA-E-159 metrics.ts `section`). The ids sit on the
 * dashboard page (ITP-E-149-06): Users & adoption, Licences & revenue, the Needs attention band, Deeper analysis and
 * Product quality.
 */
export const KPI_SECTIONS: {[key: string]: string} = {
  'active-companies': 'adb-section-users',
  'active-users': 'adb-section-users',
  'arr': 'adb-section-licences',
  'renewals-due': 'adb-section-licences',
  'revenue-at-risk': 'adb-section-attention',
  'accounts-at-risk': 'adb-section-attention',
  'trial-conversion': 'adb-section-analysis',
  'xatbot-spend': 'adb-section-quality'
};

/**
 * One Admin dashboard KPI, laid out like the OCA-E-159 tile (ITP-E-149-03): the label on one line; the value in the
 * brand font with its unit beside it ("of 887", "2 accounts"); ONE delta line coloured by whether the change is good
 * for this KPI ("▲ 3.2% vs previous 30 days", "No change …", "No comparison for this period"), or the
 * collecting-history note in its place; and the sparkline edge to edge along the bottom. A KPI with no meaning in the
 * scope (`notApplicable`, e.g. ARR for non-clients) keeps its label and shows "No Client Manager record" in place of
 * the rest, never a misleading $0.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-kpi-tile',
  templateUrl: './admin-dashboard-kpi-tile.component.html',
  styleUrls: ['./admin-dashboard-kpi-tile.component.scss']
})
export class AdminDashboardKpiTileComponent {
  @Input() kpi: AdminDashboardKpi;
  /** The dashboard period ('7d' | '30d' | '90d' | '12m'), named on the delta line ("vs previous 30 days"). */
  @Input() period: string | null = null;

  /**
   * True on the dashboard's KPI strip: a tile whose KPI has a section (KPI_SECTIONS) is then a button that asks for
   * it. False by default, so a tile reused elsewhere (Company 360) stays a plain tile.
   */
  @Input() selectable = false;
  /** The id of the section to scroll to, when the tile is clicked. */
  @Output() sectionRequested = new EventEmitter<string>();

  /** The section this KPI's tile scrolls to; null for a KPI with none. */
  get section(): string | null {
    return this.kpi ? KPI_SECTIONS[this.kpi.key] ?? null : null;
  }

  /** One sparkline point in the KPI's unit, for its tooltip ("$12,500", "25%", "865"). */
  readonly formatPoint = (value: number): string => kpiValueText({...this.kpi, value});

  /** good / bad / neutral: the colour of the delta line and the sparkline. */
  get tone(): KpiTone {
    return kpiTone(this.kpi);
  }

  /** "▲ 3.2% vs previous 30 days", "No change vs previous 30 days", "No comparison for this period". */
  get deltaText(): string {
    return kpiDeltaText(this.kpi, this.period);
  }

  /** "Collecting history — trend available from 20 Sep 2026", or null once the history covers the window. */
  get collectingText(): string | null {
    return kpiCollectingText(this.kpi);
  }

  /** "$12,500", "25%", "865", or "—" with no data. */
  get valueText(): string {
    return kpiValueText(this.kpi);
  }

  /**
   * The unit beside the value: "of 887", "2 accounts", "1 of 4 trials"; then "as of 7 Oct" when the KPI describes a
   * day before today (ITP-E-211-03), e.g. "of 12 companies · as of 7 Oct"; null without either.
   */
  get unitText(): string | null {
    const parts = [kpiUnitText(this.kpi), kpiAsOfText(this.kpi)].filter(part => !!part);
    return parts.length ? parts.join(' · ') : null;
  }

  /** What the sparkline's value counts, in its tooltip: the label in lower case ("active companies"). */
  get sparkName(): string {
    return (this.kpi?.label ?? '').toLowerCase();
  }
}
